import { CombinedGraphQLErrors } from '@apollo/client';
import { ApolloLink } from '@apollo/client/core';
import { setContext } from '@apollo/client/link/context';
import { onError } from '@apollo/client/link/error';
import { getMainDefinition } from '@apollo/client/utilities';
import { isNestJsGraphQLError, logErrorMessages } from '@pcg/graphql-kit';

import { generateRequestId } from '@ohmybible/core';
import { AppMeDocument } from '@ohmybible/graphql';

import { goToLogin } from '@/shared/tools/auth';

export const apolloRequestIdLink = setContext((_, { headers }) => ({
  headers: {
    ...headers,
    'x-request-id': generateRequestId(),
  },
}));

/** `?operation=<name>` on the URL, so API logs and the network panel name each request. */
export const apolloOperationNameLink = new ApolloLink((operation, forward) => {
  operation.setContext(() => ({
    uri: `${import.meta.env.VITE_OMB_API_GQL_URL}?operation=${operation.operationName}`,
  }));

  return forward(operation);
});

/**
 * A dead session anywhere sends the user to sign in again. Not for:
 * - `AppMe`: the auth guard asks it on every navigation and decides itself —
 *   a redirect from here would cancel the navigation in flight (a deep link
 *   would lose its target, a magic link its token);
 * - `AUTH_2FA_PENDING_TOKEN_INVALID`: that 401 belongs to a visitor who has no
 *   session yet — the sign-in pages turn it into "start over".
 */
// From the document, so renaming the query can't silently disable the skip.
const APP_ME_OPERATION = getMainDefinition(AppMeDocument).name?.value;

export const apolloErrorLink = onError((handlerOptions) => {
  const { error, operation } = handlerOptions;

  if (!CombinedGraphQLErrors.is(error) || operation.operationName === APP_ME_OPERATION) {
    return;
  }

  const unauthenticated = error.errors.some(
    (err) =>
      isNestJsGraphQLError(err)
      && err.extensions.key !== 'AUTH_2FA_PENDING_TOKEN_INVALID'
      && (err.extensions.statusCode === 401
        || err.extensions.key === 'SESSION_INVALID'
        || err.extensions.key === 'SESSION_EXPIRED'
        || err.extensions.key === 'ACCESS_TOKEN_EXPIRED'
        || err.extensions.key === 'ACCESS_TOKEN_INVALID'),
  );

  if (unauthenticated) {
    goToLogin();

    return;
  }

  if (error.errors.some((err) => isNestJsGraphQLError(err))) {
    logErrorMessages(handlerOptions);
  }
});
